import { motion } from 'framer-motion';
import {
    ArrowLeft,
    BadgeCheck,
    CalendarDays,
    CheckCircle2,
    CircleOff,
    Clock3,
    Copy,
    CreditCard,
    FileText,
    FlaskConical,
    IndianRupee,
    Link2,
    LoaderCircle,
    Package,
    Pencil,
    Plus,
    ReceiptText,
    RefreshCcw,
    Save,
    ShieldCheck,
    Tag,
    Trash2,
    Wallet,
    X,
} from 'lucide-react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BASE_URL_API } from '../../References/URLs';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import AuthContext from '../../store/auth-context';
import styles from './EachExpense.module.css';

const CATEGORY_ICON_MAP = {
    FlaskConical,
    Package,
    Tag,
    Wallet,
    CreditCard,
};

const PAYMENT_FIELDS = [
    { key: 'paidViaCash', label: 'Cash', icon: Wallet },
    { key: 'paidViaQr', label: 'QR / UPI', icon: BadgeCheck },
    { key: 'paidViaOnline', label: 'Online Transfer', icon: Link2 },
    { key: 'paidViaCheque', label: 'Cheque', icon: FileText },
    { key: 'paidViaCard', label: 'Card', icon: CreditCard },
    { key: 'paidViaCredit', label: 'Credit / Advance', icon: ReceiptText },
];

const MAX_CURRENCY_VALUE = 99999999.99;
const MAX_GST_RATE = 100;
const CATEGORY_FALLBACK_COLOR = '#888780';

const notifyExpenseMutation = () => {
    window.dispatchEvent(new CustomEvent('expenses-dashboard:refresh'));
};

let tempItemSequence = 1;
const newTempItemId = () => `new_${Date.now()}_${tempItemSequence++}`;

const parseStore = (activeStore) => {
    try {
        return (activeStore && activeStore.trim() !== '') ? JSON.parse(activeStore) : null;
    } catch {
        return null;
    }
};

const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const fmtCurrency = (value) => (
    toNumber(value).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
);

const fmtDate = (value) => {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (value) => {
    if (!value) return '—';
    const parsed = new Date(String(value).replace(' ', 'T'));
    if (Number.isNaN(parsed.getTime())) return String(value);
    return parsed.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const titleCase = (value) => {
    if (!value) return '—';
    return String(value)
        .split('_')
        .join(' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const resolveAttachmentLink = (attachmentUrl) => {
    if (!attachmentUrl) return '';
    if (/^https?:\/\//i.test(attachmentUrl)) return attachmentUrl;
    const baseHost = BASE_URL_API.includes('/andromeda/apis/')
        ? BASE_URL_API.split('/andromeda/apis/')[0]
        : BASE_URL_API;
    return `${baseHost.replace(/\/$/, '')}/${String(attachmentUrl).replace(/^\/+/, '')}`;
};

const normalizeMoneyString = (value) => {
    const num = toNumber(value);
    return num.toFixed(2);
};

const sanitizeDecimalInput = (
    rawValue,
    { maxIntegerDigits, maxDecimalDigits = 2, maxValue }
) => {
    if (rawValue === '') return '';

    let nextValue = String(rawValue).replace(/[^\d.]/g, '');
    if (!nextValue) return '';

    const firstDotIndex = nextValue.indexOf('.');
    if (firstDotIndex !== -1) {
        nextValue = `${nextValue.slice(0, firstDotIndex + 1)}${nextValue.slice(firstDotIndex + 1).replace(/\./g, '')}`;
    }

    if (nextValue.startsWith('.')) {
        nextValue = `0${nextValue}`;
    }

    const hasDot = nextValue.includes('.');
    let [integerPart, decimalPart = ''] = nextValue.split('.');

    integerPart = integerPart.replace(/^0+(?=\d)/, '');
    if (integerPart === '') integerPart = '0';
    integerPart = integerPart.slice(0, maxIntegerDigits);
    decimalPart = decimalPart.slice(0, maxDecimalDigits);

    let sanitizedValue = hasDot ? `${integerPart}.${decimalPart}` : integerPart;
    if (hasDot && decimalPart === '') {
        sanitizedValue = `${integerPart}.`;
    }

    const numericValue = parseFloat(sanitizedValue);
    if (!Number.isNaN(numericValue) && numericValue > maxValue) {
        sanitizedValue = maxValue.toFixed(maxDecimalDigits);
    }

    return sanitizedValue;
};

const sanitizeMoneyInput = (value) => sanitizeDecimalInput(value, {
    maxIntegerDigits: 8,
    maxDecimalDigits: 2,
    maxValue: MAX_CURRENCY_VALUE,
});

const sanitizeGstInput = (value) => sanitizeDecimalInput(value, {
    maxIntegerDigits: 3,
    maxDecimalDigits: 2,
    maxValue: MAX_GST_RATE,
});

const toIsoToday = () => new Date().toISOString().split('T')[0];

const normalizeExistingItem = (item) => ({
    expenseItemIdentifier: item?.expenseItemIdentifier || '',
    expenseName: String(item?.expenseName || ''),
    storeCategoryIdentifier: item?.category?.storeCategoryIdentifier || '',
    storeSubcategoryIdentifier: item?.subcategory?.storeSubcategoryIdentifier || '',
    amount: normalizeMoneyString(item?.amount),
    gstRate: normalizeMoneyString(item?.gstRate),
    expenseNotes: String(item?.expenseNotes || ''),
});

const buildEditDraft = (expense) => {
    const activeItems = (Array.isArray(expense?.items) ? expense.items : []).filter((item) => Number(item?.isActive) === 1);

    const billFields = {
        expenseDate: expense?.expenseDate || toIsoToday(),
        vendorIdentifier: String(expense?.vendorIdentifier || ''),
        vendorInvoiceNumber: String(expense?.vendorInvoiceNumber || ''),
        expenseNotes: String(expense?.expenseNotes || ''),
        attachmentUrl: String(expense?.attachmentUrl || ''),
    };

    const paymentFields = PAYMENT_FIELDS.reduce((acc, mode) => {
        acc[mode.key] = normalizeMoneyString(expense?.payment?.[mode.key]);
        return acc;
    }, {});

    const existingItems = activeItems.map((item) => normalizeExistingItem(item));

    return {
        billFields,
        paymentFields,
        existingItems,
        newItems: [],
        removedItemIds: [],
    };
};

const countEffectiveActiveAfterSave = (draft) => {
    const removedSet = new Set(draft.removedItemIds);
    const remainingExisting = draft.existingItems.filter((item) => !removedSet.has(item.expenseItemIdentifier)).length;
    const added = draft.newItems.length;
    return remainingExisting + added;
};

const flagClassName = (value) => (value ? styles.flagYes : styles.flagNo);

const EachExpenseEditor = () => {
    const { expenseIdentifier = '' } = useParams();
    const navigate = useNavigate();
    const authCtx = useContext(AuthContext);
    const asCtx = useContext(ActiveStoreContext);
    const activeStore = parseStore(asCtx?.activeStore);
    const storeIdentifier = activeStore?.storeIdentifier || '';

    const [expense, setExpense] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showRemovedItems, setShowRemovedItems] = useState(false);

    const [isEditMode, setIsEditMode] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeletingExpense, setIsDeletingExpense] = useState(false);
    const [deletingItemId, setDeletingItemId] = useState('');
    const [editDraft, setEditDraft] = useState(null);

    const [categories, setCategories] = useState([]);
    const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);

    const fetchExpenseDetails = useCallback(async ({ isRefresh = false } = {}) => {
        if (!storeIdentifier || !expenseIdentifier) {
            setExpense(null);
            setErrorMessage('Missing store or expense identifier.');
            setIsLoading(false);
            return;
        }

        if (isRefresh) {
            setIsRefreshing(true);
        } else {
            setIsLoading(true);
        }
        setErrorMessage('');

        try {
            const response = await fetch(BASE_URL_API + 'fetchExpenseDetails', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier,
                    expenseIdentifier,
                }),
            });
            const result = await response.json();

            if (result?.status === 'success') {
                setExpense(result?.response?.expense || null);
                setShowRemovedItems(false);
                return;
            }

            if (result?.message === 'tokenExpired') {
                authCtx.logout();
                return;
            }

            const msg = result?.message || 'Unable to fetch expense details.';
            setExpense(null);
            setErrorMessage(msg);
            if (isRefresh) {
                toast.error(msg, { position: 'top-center', hideProgressBar: true, autoClose: 2800 });
            }
        } catch {
            const msg = 'Unable to connect to server.';
            setExpense(null);
            setErrorMessage(msg);
            if (isRefresh) {
                toast.error(msg, { position: 'top-center', hideProgressBar: true, autoClose: 2800 });
            }
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    }, [authCtx, expenseIdentifier, storeIdentifier]);

    const fetchCategories = useCallback(async () => {
        if (!storeIdentifier) return;
        setIsCategoriesLoading(true);
        try {
            const response = await fetch(BASE_URL_API + 'fetchStoreExpensesCategoryData', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx?.token, storeIdentifier, includeInactive: false }),
            });
            const result = await response.json();
            if (result?.status === 'success') {
                setCategories(Array.isArray(result?.response?.categories) ? result.response.categories : []);
            } else if (result?.message === 'tokenExpired') {
                authCtx.logout();
            } else {
                setCategories([]);
            }
        } catch {
            setCategories([]);
        } finally {
            setIsCategoriesLoading(false);
        }
    }, [authCtx, storeIdentifier]);

    useEffect(() => {
        fetchExpenseDetails({ isRefresh: false });
    }, [fetchExpenseDetails]);

    const items = useMemo(() => (Array.isArray(expense?.items) ? expense.items : []), [expense?.items]);
    const activeItems = useMemo(() => items.filter((item) => Number(item?.isActive) === 1), [items]);
    const removedItems = useMemo(() => items.filter((item) => Number(item?.isActive) === 0), [items]);

    const payment = expense?.payment || {};

    const attachmentLink = resolveAttachmentLink(expense?.attachmentUrl);

    const copyExpenseId = async () => {
        if (!expense?.expenseIdentifier || !navigator?.clipboard) return;
        try {
            await navigator.clipboard.writeText(expense.expenseIdentifier);
            toast.success('Expense ID copied.', { position: 'top-center', hideProgressBar: true, autoClose: 1500 });
        } catch {
            toast.info('Unable to copy right now.', { position: 'top-center', hideProgressBar: true, autoClose: 1500 });
        }
    };

    const startEditing = async () => {
        if (!expense?.summary?.canEdit) {
            toast.info('This expense is locked for editing.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
            return;
        }

        setEditDraft(buildEditDraft(expense));
        setIsEditMode(true);

        if (categories.length === 0) {
            await fetchCategories();
        }
    };

    const cancelEditing = () => {
        setIsEditMode(false);
        setEditDraft(null);
    };

    const changeBillField = (field, value) => {
        setEditDraft((prev) => ({
            ...prev,
            billFields: {
                ...prev.billFields,
                [field]: value,
            },
        }));
    };

    const changePaymentField = (field, value) => {
        setEditDraft((prev) => ({
            ...prev,
            paymentFields: {
                ...prev.paymentFields,
                [field]: sanitizeMoneyInput(value),
            },
        }));
    };

    const changeExistingItemField = (expenseItemIdentifier, field, value) => {
        setEditDraft((prev) => ({
            ...prev,
            existingItems: prev.existingItems.map((item) => {
                if (item.expenseItemIdentifier !== expenseItemIdentifier) return item;
                const nextValue = field === 'amount'
                    ? sanitizeMoneyInput(value)
                    : field === 'gstRate'
                        ? sanitizeGstInput(value)
                        : value;

                const nextItem = {
                    ...item,
                    [field]: nextValue,
                };

                if (field === 'storeCategoryIdentifier') {
                    nextItem.storeSubcategoryIdentifier = '';
                }

                return nextItem;
            }),
        }));
    };

    const toggleRemoveExistingItem = (expenseItemIdentifier) => {
        setEditDraft((prev) => {
            const exists = prev.removedItemIds.includes(expenseItemIdentifier);
            return {
                ...prev,
                removedItemIds: exists
                    ? prev.removedItemIds.filter((id) => id !== expenseItemIdentifier)
                    : [...prev.removedItemIds, expenseItemIdentifier],
            };
        });
    };

    const addNewItem = () => {
        setEditDraft((prev) => ({
            ...prev,
            newItems: [
                ...prev.newItems,
                {
                    tempId: newTempItemId(),
                    expenseName: '',
                    storeCategoryIdentifier: '',
                    storeSubcategoryIdentifier: '',
                    amount: '',
                    gstRate: '0.00',
                    expenseNotes: '',
                },
            ],
        }));
    };

    const removeNewItem = (tempId) => {
        setEditDraft((prev) => ({
            ...prev,
            newItems: prev.newItems.filter((item) => item.tempId !== tempId),
        }));
    };

    const changeNewItemField = (tempId, field, value) => {
        setEditDraft((prev) => ({
            ...prev,
            newItems: prev.newItems.map((item) => {
                if (item.tempId !== tempId) return item;

                const nextValue = field === 'amount'
                    ? sanitizeMoneyInput(value)
                    : field === 'gstRate'
                        ? sanitizeGstInput(value)
                        : value;

                const nextItem = {
                    ...item,
                    [field]: nextValue,
                };

                if (field === 'storeCategoryIdentifier') {
                    nextItem.storeSubcategoryIdentifier = '';
                }

                return nextItem;
            }),
        }));
    };

    const sortedCategories = useMemo(() => (
        categories
            .filter((category) => Number(category?.isActive) === 1)
            .sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0))
    ), [categories]);

    const subcategoryByCategory = useMemo(() => {
        const map = {};
        sortedCategories.forEach((category) => {
            map[category.storeCategoryIdentifier] = (Array.isArray(category?.subcategories) ? category.subcategories : [])
                .filter((subcategory) => Number(subcategory?.isActive) === 1)
                .sort((a, b) => Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0));
        });
        return map;
    }, [sortedCategories]);

    const draftVisibleExistingItems = useMemo(() => {
        if (!editDraft) return [];
        const removedSet = new Set(editDraft.removedItemIds);
        return editDraft.existingItems.filter((item) => !removedSet.has(item.expenseItemIdentifier));
    }, [editDraft]);

    const draftTotals = useMemo(() => {
        if (!editDraft) return null;

        const removedSet = new Set(editDraft.removedItemIds);
        const totalFromExisting = editDraft.existingItems
            .filter((item) => !removedSet.has(item.expenseItemIdentifier))
            .reduce((sum, item) => sum + toNumber(item.amount) + ((toNumber(item.amount) * toNumber(item.gstRate)) / 100), 0);

        const totalFromNew = editDraft.newItems
            .reduce((sum, item) => sum + toNumber(item.amount) + ((toNumber(item.amount) * toNumber(item.gstRate)) / 100), 0);

        const totalExpense = totalFromExisting + totalFromNew;
        const totalPaid = PAYMENT_FIELDS.reduce((sum, field) => sum + toNumber(editDraft.paymentFields[field.key]), 0);

        return {
            draftTotalExpense: totalExpense,
            draftTotalPaid: totalPaid,
            draftBalanceDue: totalExpense - totalPaid,
        };
    }, [editDraft]);

    const buildEditPayload = () => {
        if (!editDraft || !expense) return null;

        const payload = {
            token: authCtx?.token,
            storeIdentifier,
            authIdentifier: authCtx?.authIdentifier,
            expenseIdentifier,
        };

        const originalBillFields = {
            expenseDate: String(expense?.expenseDate || ''),
            vendorIdentifier: String(expense?.vendorIdentifier || ''),
            vendorInvoiceNumber: String(expense?.vendorInvoiceNumber || ''),
            expenseNotes: String(expense?.expenseNotes || ''),
            attachmentUrl: String(expense?.attachmentUrl || ''),
        };

        const changedBillEntries = Object.entries(editDraft.billFields)
            .filter(([key, value]) => String(value ?? '') !== originalBillFields[key]);

        if (changedBillEntries.length > 0) {
            payload.billFields = {};
            changedBillEntries.forEach(([key, value]) => {
                payload.billFields[key] = String(value ?? '');
            });
        }

        const paymentChanged = PAYMENT_FIELDS.some((mode) => (
            normalizeMoneyString(editDraft.paymentFields[mode.key]) !== normalizeMoneyString(expense?.payment?.[mode.key])
        ));

        if (paymentChanged) {
            payload.paymentFields = PAYMENT_FIELDS.reduce((acc, mode) => {
                acc[mode.key] = toNumber(editDraft.paymentFields[mode.key]);
                return acc;
            }, {});
        }

        const originalActiveMap = new Map(
            activeItems.map((item) => [item?.expenseItemIdentifier, normalizeExistingItem(item)])
        );

        const removedSet = new Set(editDraft.removedItemIds);
        const editedItems = [];

        editDraft.existingItems
            .filter((item) => !removedSet.has(item.expenseItemIdentifier))
            .forEach((item) => {
                const original = originalActiveMap.get(item.expenseItemIdentifier);
                if (!original) return;

                const patch = { expenseItemIdentifier: item.expenseItemIdentifier };
                let hasItemDiff = false;

                const stringFields = ['expenseName', 'storeCategoryIdentifier', 'storeSubcategoryIdentifier', 'expenseNotes'];
                stringFields.forEach((field) => {
                    if (String(item[field] ?? '') !== String(original[field] ?? '')) {
                        patch[field] = String(item[field] ?? '');
                        hasItemDiff = true;
                    }
                });

                if (normalizeMoneyString(item.amount) !== normalizeMoneyString(original.amount)) {
                    patch.amount = toNumber(item.amount);
                    hasItemDiff = true;
                }

                if (normalizeMoneyString(item.gstRate) !== normalizeMoneyString(original.gstRate)) {
                    patch.gstRate = toNumber(item.gstRate);
                    hasItemDiff = true;
                }

                if (hasItemDiff) {
                    editedItems.push(patch);
                }
            });

        const addedItems = editDraft.newItems.map((item) => {
            const payloadItem = {
                expenseName: String(item.expenseName || ''),
                storeCategoryIdentifier: String(item.storeCategoryIdentifier || ''),
                amount: toNumber(item.amount),
                gstRate: toNumber(item.gstRate),
                expenseNotes: String(item.expenseNotes || ''),
            };

            if (item.storeSubcategoryIdentifier) {
                payloadItem.storeSubcategoryIdentifier = String(item.storeSubcategoryIdentifier);
            }

            return payloadItem;
        });

        if (editedItems.length > 0 || addedItems.length > 0 || editDraft.removedItemIds.length > 0) {
            payload.items = {};
            if (editedItems.length > 0) payload.items.edit = editedItems;
            if (addedItems.length > 0) payload.items.add = addedItems;
            if (editDraft.removedItemIds.length > 0) payload.items.remove = editDraft.removedItemIds;
        }

        return payload;
    };

    const validateDraftBeforeSave = () => {
        if (!editDraft) return false;

        if (editDraft.billFields.expenseDate && !/^\d{4}-\d{2}-\d{2}$/.test(editDraft.billFields.expenseDate)) {
            toast.error('Expense date must be in YYYY-MM-DD format.', { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
            return false;
        }

        const effectiveActiveCount = countEffectiveActiveAfterSave(editDraft);
        if (effectiveActiveCount <= 0) {
            toast.error('Cannot remove all items. Add at least one replacement item.', { position: 'top-center', hideProgressBar: true, autoClose: 2800 });
            return false;
        }

        const removedSet = new Set(editDraft.removedItemIds);
        const visibleExisting = editDraft.existingItems.filter((item) => !removedSet.has(item.expenseItemIdentifier));

        for (const item of visibleExisting) {
            if (!item.expenseName.trim()) {
                toast.error('Existing item name cannot be empty.', { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
                return false;
            }
            if (!item.storeCategoryIdentifier) {
                toast.error('Existing item category is required.', { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
                return false;
            }
            if (toNumber(item.amount) <= 0) {
                toast.error('Existing item amount must be greater than 0.', { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
                return false;
            }
            if (toNumber(item.gstRate) < 0 || toNumber(item.gstRate) > MAX_GST_RATE) {
                toast.error('Existing item GST must be between 0 and 100.', { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
                return false;
            }
        }

        for (const [index, item] of editDraft.newItems.entries()) {
            if (!item.expenseName.trim()) {
                toast.error(`New item ${index + 1}: expense name required.`, { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
                return false;
            }
            if (!item.storeCategoryIdentifier) {
                toast.error(`New item ${index + 1}: category required.`, { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
                return false;
            }
            if (toNumber(item.amount) <= 0) {
                toast.error(`New item ${index + 1}: amount must be greater than 0.`, { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
                return false;
            }
            if (toNumber(item.gstRate) < 0 || toNumber(item.gstRate) > MAX_GST_RATE) {
                toast.error(`New item ${index + 1}: GST must be between 0 and 100.`, { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
                return false;
            }
        }

        return true;
    };

    const saveExpenseEdits = async () => {
        if (!expense?.summary?.canEdit) {
            toast.error('Cannot edit this expense.', { position: 'top-center', hideProgressBar: true, autoClose: 2600 });
            return;
        }

        if (!validateDraftBeforeSave()) {
            return;
        }

        const payload = buildEditPayload();
        if (!payload) return;

        const hasAnySection = Boolean(payload.billFields || payload.paymentFields || payload.items);
        if (!hasAnySection) {
            toast.info('No changes detected.', { position: 'top-center', hideProgressBar: true, autoClose: 2200 });
            return;
        }

        setIsSaving(true);

        try {
            const response = await fetch(BASE_URL_API + 'editExpense', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            const result = await response.json();

            if (result?.status !== 'success') {
                if (result?.message === 'tokenExpired') {
                    authCtx.logout();
                    return;
                }

                const isLockedCase = result?.response?.paymentStatus === 'paid' && result?.response?.approvalStatus === 'approved';
                if (isLockedCase) {
                    setIsEditMode(false);
                    setEditDraft(null);
                }

                toast.error(result?.message || 'Unable to save expense changes.', { position: 'top-center', hideProgressBar: true, autoClose: 3200 });
                return;
            }

            const changes = result?.response?.changes || {};
            const parts = [];
            if (changes.billFieldsUpdated) parts.push('bill updated');
            if (changes.paymentFieldsUpdated) parts.push('payment updated');
            if (Number(changes.itemsEdited) > 0) parts.push(`${changes.itemsEdited} item(s) edited`);
            if (Number(changes.itemsAdded) > 0) parts.push(`${changes.itemsAdded} item(s) added`);
            if (Number(changes.itemsRemoved) > 0) parts.push(`${changes.itemsRemoved} item(s) removed`);

            toast.success(`Expense saved${parts.length ? ` — ${parts.join(', ')}` : ''}`, {
                position: 'top-center',
                hideProgressBar: true,
                autoClose: 3000,
            });

            notifyExpenseMutation();
            await fetchExpenseDetails({ isRefresh: true });
            setIsEditMode(false);
            setEditDraft(null);
        } catch {
            toast.error('Unable to connect to server.', { position: 'top-center', hideProgressBar: true, autoClose: 2800 });
        } finally {
            setIsSaving(false);
        }
    };

    const runDeleteExpense = async ({ target, expenseItemIdentifier: deletingExpenseItemIdentifier }) => {
        if (!expense?.summary?.canDelete) {
            const blockedReason = toNumber(expense?.payment?.totalPaid) > 0
                ? `₹${fmtCurrency(expense?.payment?.totalPaid)} is already paid. Reverse payments before deleting.`
                : 'This expense cannot be deleted right now.';
            toast.error(blockedReason, { position: 'top-center', hideProgressBar: true, autoClose: 3200 });
            return;
        }

        if (target === 'expense') {
            const confirmed = window.confirm('Delete this expense and all active line items? This removes it from active views.');
            if (!confirmed) return;
            setIsDeletingExpense(true);
        } else {
            const confirmed = window.confirm('Delete this line item? Totals will be recomputed automatically.');
            if (!confirmed) return;
            setDeletingItemId(deletingExpenseItemIdentifier || 'pending');
        }

        try {
            const payload = {
                token: authCtx?.token,
                storeIdentifier,
                authIdentifier: authCtx?.authIdentifier,
                expenseIdentifier,
                target,
            };
            if (target === 'item') payload.expenseItemIdentifier = deletingExpenseItemIdentifier;

            const response = await fetch(BASE_URL_API + 'deleteExpense', {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            const result = await response.json();

            if (result?.status !== 'success') {
                if (result?.message === 'tokenExpired') {
                    authCtx.logout();
                    return;
                }

                const totalPaid = toNumber(result?.response?.totalPaid);
                if (totalPaid > 0) {
                    toast.error(`Cannot delete. ₹${fmtCurrency(totalPaid)} payment exists. Reverse payments first.`, {
                        position: 'top-center',
                        hideProgressBar: true,
                        autoClose: 3600,
                    });
                } else {
                    toast.error(result?.message || 'Unable to delete expense.', { position: 'top-center', hideProgressBar: true, autoClose: 3200 });
                }
                return;
            }

            if (target === 'expense') {
                const deactivated = Number(result?.response?.itemsDeactivated || 0);
                toast.success(`Expense deleted. ${deactivated} item(s) removed.`, {
                    position: 'top-center',
                    hideProgressBar: true,
                    autoClose: 2800,
                });
                notifyExpenseMutation();
                navigate('/expenses/history');
                return;
            }

            const billAutoDeleted = Boolean(result?.response?.billAutoDeleted);
            if (billAutoDeleted) {
                toast.success('Last item removed — expense bill deleted.', {
                    position: 'top-center',
                    hideProgressBar: true,
                    autoClose: 2800,
                });
                notifyExpenseMutation();
                navigate('/expenses/history');
                return;
            }

            const activeItemsRemaining = Number(result?.response?.recomputed?.activeItemsRemaining || 0);
            toast.success(`Item removed. ${activeItemsRemaining} item(s) remaining.`, {
                position: 'top-center',
                hideProgressBar: true,
                autoClose: 2600,
            });

            notifyExpenseMutation();
            await fetchExpenseDetails({ isRefresh: true });
        } catch {
            toast.error('Unable to connect to server.', { position: 'top-center', hideProgressBar: true, autoClose: 2800 });
        } finally {
            setIsDeletingExpense(false);
            setDeletingItemId('');
        }
    };

    const paymentStatus = expense?.payment?.paymentStatus || 'unpaid';
    const approvalStatus = expense?.meta?.approvalStatus || 'pending';

    if (!storeIdentifier) {
        return (
            <div className={styles.noStoreWrapper}>
                <div className={styles.noStoreCard}>
                    <ReceiptText size={30} className={styles.noStoreIcon} />
                    <h3>No Store Selected</h3>
                    <p>Select a store from the top switcher to view expense details.</p>
                    <Link to="/expenses/history" className={styles.backBtn}>Back to Expenses History</Link>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.mainWrapper}>
            <motion.div
                className={styles.headerCard}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22 }}
            >
                <div className={styles.headerRowTop}>
                    <Link to="/expenses/history" className={styles.backLink}><ArrowLeft size={13} /> Back</Link>
                    <div className={styles.headerActions}>
                        {expense?.summary?.canEdit && !isEditMode && !isLoading && (
                            <button type="button" className={styles.smallBtn} onClick={startEditing}>
                                <Pencil size={13} /> Edit Expense
                            </button>
                        )}

                        {expense?.summary?.canDelete && !isEditMode && !isLoading && (
                            <button
                                type="button"
                                className={styles.dangerTinyBtn}
                                onClick={() => runDeleteExpense({ target: 'expense' })}
                                disabled={isDeletingExpense || isRefreshing || isSaving}
                            >
                                {isDeletingExpense ? <LoaderCircle size={13} className={styles.spin} /> : <Trash2 size={13} />} Delete Expense
                            </button>
                        )}

                        {isEditMode && (
                            <>
                                <button type="button" className={styles.smallBtn} onClick={cancelEditing} disabled={isSaving}>
                                    <X size={13} /> Cancel
                                </button>
                                <button type="button" className={styles.primaryTinyBtn} onClick={saveExpenseEdits} disabled={isSaving}>
                                    {isSaving ? <LoaderCircle size={13} className={styles.spin} /> : <Save size={13} />} Save Changes
                                </button>
                            </>
                        )}

                        <button
                            type="button"
                            className={styles.smallBtn}
                            onClick={() => fetchExpenseDetails({ isRefresh: true })}
                            disabled={isLoading || isRefreshing || isSaving || isDeletingExpense || deletingItemId !== ''}
                        >
                            <RefreshCcw size={13} className={isRefreshing ? styles.spin : ''} /> Refresh
                        </button>
                    </div>
                </div>

                <div className={styles.headerTitleRow}>
                    <h1>Expense Details</h1>
                    {expense?.expenseIdentifier && (
                        <button type="button" className={styles.idChip} onClick={copyExpenseId}>
                            <span>{expense.expenseIdentifier}</span>
                            <Copy size={12} />
                        </button>
                    )}
                </div>

                <div className={styles.metaChips}>
                    <span className={`${styles.metaChip} ${styles[paymentStatus] || styles.unpaid}`}>Payment: {titleCase(paymentStatus)}</span>
                    <span className={`${styles.metaChip} ${styles[approvalStatus] || styles.pending}`}>Approval: {titleCase(approvalStatus)}</span>
                    <span className={styles.metaChip}>Type: {String(expense?.meta?.expenseType || '—').toUpperCase()}</span>
                    <span className={styles.metaChip}><Package size={12} /> Active Items: {expense?.summary?.activeItems ?? activeItems.length}</span>
                    {!expense?.summary?.canEdit && <span className={styles.lockedChip}>Locked for edits</span>}
                </div>
            </motion.div>

            {isLoading && (
                <div className={styles.stateCard}>
                    <LoaderCircle size={16} className={styles.spin} /> Loading expense details...
                </div>
            )}

            {!isLoading && errorMessage && (
                <div className={styles.stateCardError}>
                    <span>{errorMessage}</span>
                    <button type="button" className={styles.smallBtn} onClick={() => fetchExpenseDetails({ isRefresh: false })}>Retry</button>
                </div>
            )}

            {!isLoading && !errorMessage && expense && isEditMode && editDraft && (
                <section className={styles.editPanel}>
                    <div className={styles.editPanelHeader}>
                        <h3><Pencil size={14} /> Edit Expense</h3>
                        <span>Only changed fields are sent. Totals are server-recomputed.</span>
                    </div>

                    <div className={styles.editGrid2}>
                        <label className={styles.editField}>
                            <span>Expense Date</span>
                            <input
                                type="date"
                                value={editDraft.billFields.expenseDate}
                                max={toIsoToday()}
                                onChange={(e) => changeBillField('expenseDate', e.target.value)}
                            />
                        </label>
                        <label className={styles.editField}>
                            <span>Vendor Identifier</span>
                            <input
                                type="text"
                                value={editDraft.billFields.vendorIdentifier}
                                onChange={(e) => changeBillField('vendorIdentifier', e.target.value)}
                                placeholder="VENDOR_001"
                                maxLength={80}
                            />
                        </label>
                        <label className={styles.editField}>
                            <span>Invoice Number</span>
                            <input
                                type="text"
                                value={editDraft.billFields.vendorInvoiceNumber}
                                onChange={(e) => changeBillField('vendorInvoiceNumber', e.target.value)}
                                placeholder="INV-2025-442"
                                maxLength={80}
                            />
                        </label>
                        <label className={styles.editField}>
                            <span>Attachment URL / Key</span>
                            <input
                                type="text"
                                value={editDraft.billFields.attachmentUrl}
                                onChange={(e) => changeBillField('attachmentUrl', e.target.value)}
                                placeholder="expenses/STORE_ABC123/receipt.jpg"
                            />
                        </label>
                    </div>

                    <label className={styles.editField}>
                        <span>Bill Notes</span>
                        <textarea
                            value={editDraft.billFields.expenseNotes}
                            onChange={(e) => changeBillField('expenseNotes', e.target.value)}
                            rows={3}
                            placeholder="Add or clear expense notes"
                        />
                    </label>

                    <div className={styles.editBlock}>
                        <div className={styles.editBlockHead}><CreditCard size={13} /> Payment Fields (all 6 sent when changed)</div>
                        <div className={styles.paymentGridEdit}>
                            {PAYMENT_FIELDS.map((mode) => {
                                const Icon = mode.icon;
                                return (
                                    <label className={styles.editField} key={mode.key}>
                                        <span><Icon size={12} /> {mode.label}</span>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={editDraft.paymentFields[mode.key]}
                                            onChange={(e) => changePaymentField(mode.key, e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    <div className={styles.editBlock}>
                        <div className={styles.editItemsHead}>
                            <div className={styles.editBlockHead}><Package size={13} /> Item Editor</div>
                            <button type="button" className={styles.smallBtn} onClick={addNewItem}><Plus size={12} /> Add Item</button>
                        </div>

                        <div className={styles.editItemsList}>
                            {draftVisibleExistingItems.map((item) => (
                                <div className={styles.editItemCard} key={item.expenseItemIdentifier}>
                                    <div className={styles.editItemHead}>
                                        <strong>Existing Item</strong>
                                        <button
                                            type="button"
                                            className={styles.deleteTinyBtn}
                                            onClick={() => toggleRemoveExistingItem(item.expenseItemIdentifier)}
                                        >
                                            <Trash2 size={12} /> Remove
                                        </button>
                                    </div>

                                    <div className={styles.editGrid2}>
                                        <label className={styles.editField}>
                                            <span>Name</span>
                                            <input
                                                type="text"
                                                value={item.expenseName}
                                                onChange={(e) => changeExistingItemField(item.expenseItemIdentifier, 'expenseName', e.target.value)}
                                                maxLength={120}
                                            />
                                        </label>
                                        <label className={styles.editField}>
                                            <span>Category</span>
                                            <select
                                                value={item.storeCategoryIdentifier}
                                                onChange={(e) => changeExistingItemField(item.expenseItemIdentifier, 'storeCategoryIdentifier', e.target.value)}
                                                disabled={isCategoriesLoading}
                                            >
                                                <option value="">Select category</option>
                                                {sortedCategories.map((category) => (
                                                    <option key={category.storeCategoryIdentifier} value={category.storeCategoryIdentifier}>
                                                        {category.categoryLabel}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className={styles.editField}>
                                            <span>Subcategory</span>
                                            <select
                                                value={item.storeSubcategoryIdentifier}
                                                onChange={(e) => changeExistingItemField(item.expenseItemIdentifier, 'storeSubcategoryIdentifier', e.target.value)}
                                                disabled={!item.storeCategoryIdentifier}
                                            >
                                                <option value="">No subcategory</option>
                                                {(subcategoryByCategory[item.storeCategoryIdentifier] || []).map((subcategory) => (
                                                    <option key={subcategory.storeSubcategoryIdentifier} value={subcategory.storeSubcategoryIdentifier}>
                                                        {subcategory.subcategoryLabel}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className={styles.editField}>
                                            <span>Amount</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={item.amount}
                                                onChange={(e) => changeExistingItemField(item.expenseItemIdentifier, 'amount', e.target.value)}
                                                placeholder="0.00"
                                            />
                                        </label>
                                        <label className={styles.editField}>
                                            <span>GST %</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={item.gstRate}
                                                onChange={(e) => changeExistingItemField(item.expenseItemIdentifier, 'gstRate', e.target.value)}
                                                placeholder="0.00"
                                            />
                                        </label>
                                    </div>

                                    <label className={styles.editField}>
                                        <span>Item Notes</span>
                                        <input
                                            type="text"
                                            value={item.expenseNotes}
                                            onChange={(e) => changeExistingItemField(item.expenseItemIdentifier, 'expenseNotes', e.target.value)}
                                            placeholder="Optional"
                                        />
                                    </label>
                                </div>
                            ))}

                            {editDraft.newItems.map((item, index) => (
                                <div className={styles.editItemCardNew} key={item.tempId}>
                                    <div className={styles.editItemHead}>
                                        <strong>New Item #{index + 1}</strong>
                                        <button type="button" className={styles.deleteTinyBtn} onClick={() => removeNewItem(item.tempId)}>
                                            <Trash2 size={12} /> Remove
                                        </button>
                                    </div>

                                    <div className={styles.editGrid2}>
                                        <label className={styles.editField}>
                                            <span>Name</span>
                                            <input
                                                type="text"
                                                value={item.expenseName}
                                                onChange={(e) => changeNewItemField(item.tempId, 'expenseName', e.target.value)}
                                                maxLength={120}
                                            />
                                        </label>
                                        <label className={styles.editField}>
                                            <span>Category</span>
                                            <select
                                                value={item.storeCategoryIdentifier}
                                                onChange={(e) => changeNewItemField(item.tempId, 'storeCategoryIdentifier', e.target.value)}
                                                disabled={isCategoriesLoading}
                                            >
                                                <option value="">Select category</option>
                                                {sortedCategories.map((category) => (
                                                    <option key={category.storeCategoryIdentifier} value={category.storeCategoryIdentifier}>
                                                        {category.categoryLabel}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className={styles.editField}>
                                            <span>Subcategory</span>
                                            <select
                                                value={item.storeSubcategoryIdentifier}
                                                onChange={(e) => changeNewItemField(item.tempId, 'storeSubcategoryIdentifier', e.target.value)}
                                                disabled={!item.storeCategoryIdentifier}
                                            >
                                                <option value="">No subcategory</option>
                                                {(subcategoryByCategory[item.storeCategoryIdentifier] || []).map((subcategory) => (
                                                    <option key={subcategory.storeSubcategoryIdentifier} value={subcategory.storeSubcategoryIdentifier}>
                                                        {subcategory.subcategoryLabel}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <label className={styles.editField}>
                                            <span>Amount</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={item.amount}
                                                onChange={(e) => changeNewItemField(item.tempId, 'amount', e.target.value)}
                                                placeholder="0.00"
                                            />
                                        </label>
                                        <label className={styles.editField}>
                                            <span>GST %</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={item.gstRate}
                                                onChange={(e) => changeNewItemField(item.tempId, 'gstRate', e.target.value)}
                                                placeholder="0.00"
                                            />
                                        </label>
                                    </div>

                                    <label className={styles.editField}>
                                        <span>Item Notes</span>
                                        <input
                                            type="text"
                                            value={item.expenseNotes}
                                            onChange={(e) => changeNewItemField(item.tempId, 'expenseNotes', e.target.value)}
                                            placeholder="Optional"
                                        />
                                    </label>
                                </div>
                            ))}

                            {editDraft.removedItemIds.length > 0 && (
                                <div className={styles.removedDraftBox}>
                                    <strong>Marked for removal: {editDraft.removedItemIds.length}</strong>
                                    <div className={styles.removedDraftList}>
                                        {editDraft.removedItemIds.map((id) => (
                                            <button type="button" key={id} className={styles.undoChip} onClick={() => toggleRemoveExistingItem(id)}>
                                                Undo remove {id}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles.editDraftSummary}>
                        <span><IndianRupee size={12} /> Draft Total: ₹{fmtCurrency(draftTotals?.draftTotalExpense)}</span>
                        <span>Draft Paid: ₹{fmtCurrency(draftTotals?.draftTotalPaid)}</span>
                        <span>Draft Balance: ₹{fmtCurrency(draftTotals?.draftBalanceDue)}</span>
                    </div>
                </section>
            )}

            {!isLoading && !errorMessage && expense && (
                <div className={styles.contentGrid}>
                    <div className={styles.mainCol}>
                        <section className={styles.card}>
                            <div className={styles.cardHead}><CalendarDays size={14} /> Bill Overview</div>
                            <div className={styles.overviewGrid}>
                                <div><span>Bill Date</span><strong>{fmtDate(expense?.expenseDate)}</strong></div>
                                <div><span>Fiscal Period</span><strong>{expense?.fiscalPeriod || '—'}</strong></div>
                                <div><span>Vendor Identifier</span><strong>{expense?.vendorIdentifier || '—'}</strong></div>
                                <div><span>Invoice Number</span><strong>{expense?.vendorInvoiceNumber || '—'}</strong></div>
                                <div><span>Created On</span><strong>{fmtDateTime(expense?.meta?.createdOn)}</strong></div>
                                <div><span>Updated On</span><strong>{fmtDateTime(expense?.meta?.updatedAt)}</strong></div>
                                <div><span>Approved By</span><strong>{expense?.meta?.approvedBy || '—'}</strong></div>
                                <div><span>Approved At</span><strong>{fmtDateTime(expense?.meta?.approvedAt)}</strong></div>
                            </div>
                            {expense?.meta?.recurringExpenseIdentifier && (
                                <p className={styles.inlineHint}><Clock3 size={13} /> Recurring Series: {expense.meta.recurringExpenseIdentifier}</p>
                            )}
                            {expense?.expenseNotes && (
                                <div className={styles.notesBox}>{expense.expenseNotes}</div>
                            )}
                        </section>

                        <section className={styles.card}>
                            <div className={styles.cardHead}><Package size={14} /> Active Line Items ({activeItems.length})</div>
                            <div className={styles.itemsList}>
                                {activeItems.length === 0 && (
                                    <div className={styles.emptyInline}><CircleOff size={14} /> No active items found.</div>
                                )}

                                {activeItems.map((item) => {
                                    const CategoryIcon = CATEGORY_ICON_MAP[item?.category?.iconSlug] || Tag;
                                    const colorHex = item?.category?.colorHex || CATEGORY_FALLBACK_COLOR;
                                    return (
                                        <div key={item?.expenseItemIdentifier} className={styles.itemRow}>
                                            <div className={styles.itemTop}>
                                                <h4>{item?.expenseName || 'Untitled Item'}</h4>
                                                <div className={styles.itemTopActions}>
                                                    {expense?.summary?.canDelete && !isEditMode && (
                                                        <button
                                                            type="button"
                                                            className={styles.deleteItemBtn}
                                                            onClick={() => runDeleteExpense({ target: 'item', expenseItemIdentifier: item?.expenseItemIdentifier })}
                                                            disabled={isDeletingExpense || isSaving || isRefreshing || deletingItemId !== ''}
                                                        >
                                                            {deletingItemId === item?.expenseItemIdentifier
                                                                ? <LoaderCircle size={12} className={styles.spin} />
                                                                : <Trash2 size={12} />}
                                                        </button>
                                                    )}
                                                    <span className={styles.itemAmount}>₹{fmtCurrency(item?.totalAmount)}</span>
                                                </div>
                                            </div>

                                            <div className={styles.itemMetaRow}>
                                                <span className={styles.categoryPill} style={{ borderColor: `${colorHex}66`, backgroundColor: `${colorHex}14`, color: colorHex }}>
                                                    <CategoryIcon size={12} /> {item?.category?.categoryLabel || 'Uncategorized'}
                                                </span>
                                                <span className={styles.subcategoryPill}>{item?.subcategory?.subcategoryLabel || 'No subcategory'}</span>
                                            </div>

                                            <div className={styles.itemStats}>
                                                <span>Net ₹{fmtCurrency(item?.amount)}</span>
                                                <span>GST {toNumber(item?.gstRate)}%</span>
                                                <span>GST Amt ₹{fmtCurrency(item?.gstAmount)}</span>
                                                <span>Type {String(item?.resolvedExpenseType || '—').toUpperCase()}</span>
                                            </div>

                                            {item?.expenseNotes && <p className={styles.itemNote}>{item.expenseNotes}</p>}
                                        </div>
                                    );
                                })}
                            </div>

                            {removedItems.length > 0 && (
                                <div className={styles.removedWrap}>
                                    <button
                                        type="button"
                                        className={styles.toggleRemovedBtn}
                                        onClick={() => setShowRemovedItems((prev) => !prev)}
                                    >
                                        <Trash2 size={12} /> Removed Items ({removedItems.length})
                                    </button>

                                    {showRemovedItems && (
                                        <div className={styles.itemsListRemoved}>
                                            {removedItems.map((item) => (
                                                <div key={item?.expenseItemIdentifier} className={styles.itemRowRemoved}>
                                                    <div className={styles.itemTop}>
                                                        <h4>{item?.expenseName || 'Untitled Item'}</h4>
                                                        <span className={styles.itemAmount}>₹{fmtCurrency(item?.totalAmount)}</span>
                                                    </div>
                                                    <p className={styles.itemNote}>This item is soft-deleted (`isActive = 0`).</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    </div>

                    <div className={styles.sideCol}>
                        <section className={styles.card}>
                            <div className={styles.cardHead}><IndianRupee size={14} /> Billing Summary</div>
                            <div className={styles.statList}>
                                <div><span>Sub Total</span><strong>₹{fmtCurrency(expense?.billing?.subTotal)}</strong></div>
                                <div><span>Total GST</span><strong>₹{fmtCurrency(expense?.billing?.gstAmount)}</strong></div>
                                <div><span>Avg GST Rate</span><strong>{toNumber(expense?.billing?.gstRate)}%</strong></div>
                                <div className={styles.totalRow}><span>Total Expense</span><strong>₹{fmtCurrency(expense?.billing?.totalExpense)}</strong></div>
                            </div>
                            <p className={styles.inlineHint}>
                                <ShieldCheck size={13} /> ITC Claimed: {Number(expense?.billing?.isGstItcClaimed) === 1 ? 'Yes' : 'No'}
                            </p>
                        </section>

                        <section className={styles.card}>
                            <div className={styles.cardHead}><CreditCard size={14} /> Payment Breakdown</div>
                            <div className={styles.paymentList}>
                                {PAYMENT_FIELDS.map((mode) => {
                                    const ModeIcon = mode.icon;
                                    return (
                                        <div key={mode.key} className={styles.paymentRow}>
                                            <span><ModeIcon size={12} /> {mode.label}</span>
                                            <strong>₹{fmtCurrency(payment?.[mode.key])}</strong>
                                        </div>
                                    );
                                })}
                                <div className={styles.totalRow}><span>Total Paid</span><strong>₹{fmtCurrency(payment?.totalPaid)}</strong></div>
                                <div className={styles.balanceRow}><span>Balance Due</span><strong>₹{fmtCurrency(payment?.balanceDue)}</strong></div>
                            </div>
                        </section>

                        <section className={styles.card}>
                            <div className={styles.cardHead}><CheckCircle2 size={14} /> Action Flags</div>
                            <div className={styles.flagsList}>
                                <div className={flagClassName(expense?.summary?.canEdit)}>Can Edit: <strong>{expense?.summary?.canEdit ? 'Yes' : 'No'}</strong></div>
                                <div className={flagClassName(expense?.summary?.canDelete)}>Can Delete: <strong>{expense?.summary?.canDelete ? 'Yes' : 'No'}</strong></div>
                                <div className={flagClassName(expense?.summary?.isPendingApproval)}>Pending Approval: <strong>{expense?.summary?.isPendingApproval ? 'Yes' : 'No'}</strong></div>
                                <div className={flagClassName(expense?.summary?.isRecurring)}>Recurring Bill: <strong>{expense?.summary?.isRecurring ? 'Yes' : 'No'}</strong></div>
                                <div className={flagClassName(expense?.summary?.hasAttachment)}>Has Attachment: <strong>{expense?.summary?.hasAttachment ? 'Yes' : 'No'}</strong></div>
                                <div className={styles.flagNeutral}>Items Active: <strong>{expense?.summary?.activeItems ?? activeItems.length} / {expense?.summary?.totalItems ?? items.length}</strong></div>
                            </div>
                        </section>

                        <section className={styles.card}>
                            <div className={styles.cardHead}><Link2 size={14} /> Attachment</div>
                            {expense?.summary?.hasAttachment && attachmentLink ? (
                                <a href={attachmentLink} target="_blank" rel="noreferrer" className={styles.attachmentLink}>Open Receipt</a>
                            ) : (
                                <p className={styles.noAttachment}>No attachment available for this expense.</p>
                            )}
                        </section>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EachExpenseEditor;
